import { Inject, Component, OnInit, ViewChild, DestroyRef } from '@angular/core';
import { Customer } from '../../Model/User/Customer';
import { ModelService } from '../../Services/model.service';
import { CustomerStatistics } from '../../Model/Dto/CustomerStatistics';
import { LanguageFlag } from '../../Model/Dto/LanguageFlag';
import { TranslateService } from '@ngx-translate/core';
import { ViewService } from '../../Services/view.service';
import { OsgConnection } from '../../Model/Dto/OsgConnection';
import { SendToNWConfig } from '../../Model/Dto/SendToNWConfig';
import { WawiSettings } from '../../Model/User/WawiSettings';
import {DxDataGridComponent, DxValidationGroupComponent} from 'devextreme-angular';
import { Item as AccordionItem } from 'devextreme/ui/accordion';
import { InitializedEvent as TagBoxInitializedEvent, DisposingEvent as TagBoxDisposingEvent, ValueChangedEvent as TagBoxValueChangedEvent } from 'devextreme/ui/tag_box';
import { ClickEvent as ButtonClickEvent } from 'devextreme/ui/button';
import { UserManagementService } from '../../Services/userManagment.service';
import { CustomerService } from '../../Services/customer.service';
import { LoginService } from '../../Services/login.service';
import { AddonService } from '../../Services/addon.service';
import {
  firstValueFrom,
  lastValueFrom,
  Subscription,
  switchMap,
  tap,
  combineLatest,
  Subject,
  BehaviorSubject, Observable
} from 'rxjs';
import { LicenseService } from '../../Services/license.service';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { map } from 'rxjs/operators';
import { AddonCategory } from '../../Model/User/AddonCategory';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {License} from "../../Model/User/License";
import { LicenseSelectEntry } from "../../Model/User/LicenseSelectEntry";
import {TranslatedText} from "../../Model/ui/TranslatedText";
const MAX_INT_32 = 2147483647;

interface EditableAddonCategory extends AddonCategory {
  translatedName: string;
  summary: string;
  invalid: boolean;
  invalidTooltip?: string;
  datasource: DataSource;
  availableAddons: { addon: string; name: string }[];
  selectedAddons: string[];
  onValueChanged: (category: EditableAddonCategory, e: TagBoxValueChangedEvent) => void;
  onSelectionStart?: () => void;
  valueRollback?: boolean;
}

const dropUnauthorizedAddonFromSourceCollections = (addon: string, unauthorizedActiveAddons: Array<string>, selectableAddonsSource: Array<{ addon: string }>) => {

  let reloadSource = false;

  if (unauthorizedActiveAddons.includes(addon)) {
    unauthorizedActiveAddons.splice(unauthorizedActiveAddons.indexOf(addon), 1);

    const index = selectableAddonsSource.findIndex(a => a.addon === addon);

    if (index >= 0) {
      selectableAddonsSource.splice(index, 1);
      reloadSource = true;
    }
  }

  return reloadSource;
}


@Component({
    selector: 'myAccount',
    templateUrl: 'myAccount.component.html',
    styleUrls: ['myAccount.component.css'],
    standalone: false
})
export class MyAccountComponent implements OnInit {
  @ViewChild(DxDataGridComponent, { static: false }) dataGrid: DxDataGridComponent;
  @ViewChild(DxValidationGroupComponent, { static: false }) addAccountValidationGroup: DxValidationGroupComponent;

  public customers: Customer[];
  public licenseDatasource: DataSource;
  public licenses: LicenseSelectEntry[];

  public addonCategories: (AccordionItem & EditableAddonCategory)[] = [];
  public filteredAddonCategories: (AccordionItem & EditableAddonCategory)[] = [];
  public addonSearchText: string = '';

  public userPageWarning$: Subject<string|undefined> = new BehaviorSubject(undefined);
  public addonPageWarning$: Subject<string|undefined> = new BehaviorSubject(undefined);

  accountTypes: TranslatedText[];

  isClaimDropDownBoxOpened: boolean = false;
  loggedInSubscription: Subscription = null;
  customerStatistics: CustomerStatistics[];
  statisticsFrom: Date = this.defaultStatisticsFrom();
  statisticsTo: Date = new Date();

  private defaultStatisticsFrom(): Date {
    const from = new Date();
    from.setFullYear(from.getFullYear() - 1);
    return from;
  }

  private toDate(value: Date | string): Date {
    return value instanceof Date ? value : new Date(value);
  }

  get filteredStatistics(): CustomerStatistics[] {
    if (!this.customerStatistics) return [];
    return this.customerStatistics.filter((stat) => {
      const date = this.toDate(stat.date);
      return date >= this.statisticsFrom && date <= this.statisticsTo;
    });
  }

  get latestStatistic(): CustomerStatistics {
    if (!this.customerStatistics?.length) return null;
    return this.customerStatistics.reduce((latest, stat) =>
      this.toDate(stat.date) > this.toDate(latest.date) ? stat : latest);
  }

  formatStatNumber(value: number): string {
    return value == null ? '-' : value.toLocaleString('de-DE');
  }

  formatStatDate(value: Date | string): string {
    return value == null ? '-' : this.toDate(value).toLocaleDateString('de-DE');
  }

  customizeTooltip = (info: any) => {
    const argument = info.argument;
    const date = argument instanceof Date ? argument.toLocaleDateString('de-DE') : info.argumentText;
    return { text: `${date}\n${info.seriesName}: ${info.valueText}` };
  };

  get customer(): Customer {
    return this.modelService.loginService.currentCustomer;
  }
  set customer(val: Customer) {}

  get isAdmin(): boolean {
    return this.modelService.loginService.isAdmin;
  }

  get isManager(): boolean {
    return this.modelService.loginService.isManager;
  }

  constructor(
    @Inject(DestroyRef) private readonly destroyRef: DestroyRef,
    public modelService: ModelService,
    public translate: TranslateService,
    public userManagement: UserManagementService,
    public customerService: CustomerService,
    public loginService: LoginService,
    private licenseService: LicenseService,
    public addonService: AddonService
  ) {
    this.addCustomer = this.addCustomer.bind(this);
    this.hideAddCustomer = this.hideAddCustomer.bind(this);
    this.showAddCustomer = this.showAddCustomer.bind(this);
    this.showAddUser = this.showAddUser.bind(this);
    this.onLoggedIn = this.onLoggedIn.bind(this);
    this.onContentReadyHandler = this.onContentReadyHandler.bind(this);
    this.deleteCustomer = this.deleteCustomer.bind(this);
    this.confirmDelete = this.confirmDelete.bind(this);
    this.checkAddons = this.checkAddons.bind(this);

    this.licenseDatasource = new DataSource(new CustomStore({
      key: 'id',
      loadMode: 'raw',
      load: () => firstValueFrom(
        this.licenseService.getLicenseSelectList().pipe(
          map((licenses) => licenses.filter(l => l.active))
        )
      ),
      byKey: (key) => firstValueFrom(this.licenseService.getLicenseSelectSingleById(key))
    }));
  }

  get isNordwestTabVisible(): boolean {
    return this.loginService.hasAddonNordwest
      && this.loginService.currentUser?.currentClaim === 'ADMIN';
  }

  get isEdeSortimentsAboTabVisible(): boolean {
    return this.loginService.hasAddonEDESortimentsAbo
      && this.loginService.currentUser?.currentClaim === 'ADMIN';
  }

  get isAutoJobsTabEnabled(): boolean {
    return this.loginService.hasAddonAutoJobsExport || this.loginService.hasAddonAutoJobsImport;
  }

  get isDeeplTabVisible(): boolean {
    return this.loginService.hasAddonDEEPLTRANSLATE || this.loginService.hasAddonDEEPLTRANSLATEOWNKEY;
  }

  get isWawiTabVisible(): boolean {
    return this.loginService.hasAddonWawiErp || this.loginService.hasAddonVEO;
  }

  async ngOnInit() {
    if (this.modelService.loginService.currentUser == null) {
      this.modelService.router.navigate(['/']);
      return;
    }

    this.loginService.currentCustomer$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((customer) => combineLatest([
        this.addonService.getAllAddonCategories(),
        customer?.id ? this.licenseService.getLicenseForCustomer(customer.id) : undefined,
        customer?.id ? this.addonService.getAddonsOfCustomer(customer.id) : undefined]))
    ).subscribe(([categories, license, customerAddons]) => {
        if (license && customerAddons) {
          this.addonCategories = categories
            .filter(category => !category.adminOnly || this.modelService.loginService.isAdmin)
            .map(category => this.mapAddonCategoryToAddonEditorEntry(category, license, customerAddons));
          this.filterAddonCategories();
        }
    });

    this.licenseService.getLicenseSelectList().subscribe((licenses: LicenseSelectEntry[]) => {
      this.licenses = licenses;
    });

    this.updateCurrentCustomer();
    this.modelService.systemService.help = [];
    this.modelService.systemService.showHelp = false;

    this.modelService.systemService.currentNavigationTitle = this.translate.instant('Meine Accounts');

    this.accountTypes = [
      { value: 'Unknown', translated: this.translate.instant('kein Hub-Account') },
      { value: 'Dealer', translated: this.translate.instant('HändlerHub') },
      { value: 'Manufacturer', translated: this.translate.instant('HerstellerHub') }
    ];

    if (this.modelService.loginService.loggedIn) this.onLoggedIn();
    else this.loggedInSubscription = this.modelService.loginService.onLoggedIn.subscribe(() => this.onLoggedIn());
  }

  ngOnDestroy() {
    try {
      if (this.loggedInSubscription != null) this.loggedInSubscription.unsubscribe();
    } catch (error) {
      console.error(error);
    }
  }

  onLoggedIn() {
    this.modelService.loginService.customerService
      .getAllCustomerByUser(this.modelService.loginService.currentUser.id)
      .subscribe((result: Customer[]) => {
        this.customers = result;
      });
  }

  onGridToolbarPreparing(e) {
    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'newAccountButtonTemplate'
    });
  }

  onUserGridToolbarPreparing() {}

  get searchText() {
    return this.modelService.systemService.searchText;
  }

  set searchText(value) {
    this.modelService.systemService.searchText = value;
  }

  getLanguageText(language): string {
    return this.modelService.loginService.getLanguageText(language);
  }

  getLanguageFlag(language) {
    return this.modelService.loginService.getLanguageFlag(language);
  }

  onCustomerClick(e) {
    if (e.selectedRowsData[0].id != this.customer.id) {
      this.modelService.loginService.setCustomer(e.selectedRowsData[0].id).then(() => {
        this.updateCurrentCustomer();
      });
    }
  }

  onContentReadyHandler(e) {
    const key = e.component.getRowIndexByKey(this.modelService.loginService.currentUser.customerId);
    e.component.selectRowsByIndexes([key]);
  }

  updateCurrentCustomer() {
    this.validateLicense().subscribe();

    ///hier dann produkt updaten
  ///  this.modelService.loginService.userService.getUser()
    this.modelService.loginService.getCustomerStatistics(this.customer.id).subscribe((stats: CustomerStatistics[]) => {
      this.customerStatistics = stats.map((au: CustomerStatistics) => new CustomerStatistics().deserialize(au));
    });

    this.modelService.osgConnectionService.getConnection(this.customer.id, false).subscribe((result: OsgConnection) => {
      if (result != null) this.modelService.loginService.osgConnection = result;
      else {
        this.modelService.loginService.osgConnection = new OsgConnection();
        this.modelService.loginService.osgConnection.customerId = this.customer.id;
      }
    });

    this.modelService.osgConnectionService.getConnection(this.customer.id, true).subscribe((result: OsgConnection) => {
      if (result != null) this.modelService.loginService.osgConnectionTest = result;
      else {
        this.modelService.loginService.osgConnectionTest = new OsgConnection();
        this.modelService.loginService.osgConnectionTest.isTest = true;
        this.modelService.loginService.osgConnectionTest.customerId = this.customer.id;
      }
    });

    this.modelService.wawiSettingsService.getWawiSettings(this.customer.id).subscribe((result: WawiSettings) => {
      if (result != null) this.modelService.loginService.wawiSettings = result;
      else {
        this.modelService.loginService.wawiSettings = new WawiSettings();
        this.modelService.loginService.wawiSettings.customerId = this.customer.id;
      }
    });

    this.modelService.sendToNWConfigService.getConnection(this.customer.id).subscribe((result: SendToNWConfig) => {
      if (result != null) this.modelService.loginService.sendToNWConfig = result;
      else this.modelService.loginService.sendToNWConfig = new SendToNWConfig();
      this.modelService.loginService.sendToNWConfig.customerId = this.customer.id;
    });
  }

  updateUser(event = null) {
    if (event != null && event.event == undefined) return;

    this.modelService.loginService.CheckAndSetFrontEndLanguage();
    this.modelService.loginService.updateUser();
  }

  isCurrentCustomer(customer: Customer): boolean {
    return customer.id != this.modelService.loginService.currentUser.customerId;
  }

  readonly onCategoryAddonsChanged = async (e: TagBoxValueChangedEvent) => {
    const addedAddons: string[] = e.value.filter((x) => !e.previousValue.some((y) => y === x));
    const removedAddons: string[] = e.previousValue.filter((x) => !e.value.some((y) => y === x));

    if (addedAddons.length === 0 && removedAddons.length === 0) {
      return;
    }

    const errors = await lastValueFrom(
      this.addonService.addonSelectionChanged(this.customer.id, addedAddons, removedAddons)
    );
    if (errors.length > 0) {
      this.modelService.systemService.notifyWarning(errors.join('\n'));
    }
    const addons = await lastValueFrom(this.addonService.getAddonsOfCustomer(this.customer.id));
    await this.modelService.loginService.setAddons(addons);

    this.validateLicense().subscribe();
  }

  get possibleLanguages(): LanguageFlag[] {
    return ViewService.languages;
  }

  get possibleFrontEndLanguages(): LanguageFlag[] {
    return ViewService.frontendLanguages;
  }

  public addAccountVisible: boolean = false;
  public addAccountItem?: Customer;

  showAddCustomer() {
    this.addAccountVisible = true;
    this.addAccountItem = new Customer();
  }

  hideAddCustomer() {
    this.addAccountVisible = false;
    this.addAccountItem = null;
  }

  addCustomer() {

    if (!this.addAccountValidationGroup.instance.validate().isValid) {
      return;
    }

    this.addAccountVisible = false;
    const customer = this.addAccountItem;
    customer.defaultLanguage = 'deu';
    this.modelService.loginService.customerService.create(customer).subscribe((createdCustomer: Customer) => {
      this.modelService.loginService.customerService
        .getAllCustomerByUser(this.modelService.loginService.currentUser.id)
        .subscribe((result: Customer[]) => {
          this.customers = result;
          this.modelService.loginService.setCustomer(createdCustomer.id);
          this.updateCurrentCustomer();

          this.addAccountItem = null;
        });
    });
  }

  public addUserVisible: boolean = false;
  public newUserMail: string = '';

  showAddUser() {
    this.addUserVisible = true;
    this.newUserMail = '';
  }

  canUserEdit(user): boolean {
    if (user == this.modelService.loginService.currentUser.email) {
      return true;
    }

    return this.modelService.loginService.isManager;
  }

  public statusVisible: boolean = false;

  public toggleStatus() {
    if (this.statusVisible) {
      this.statusVisible = false;
    } else {
      this.statusVisible = true;
    }
  }

  public v: boolean[] = [false, false, false, false, false, false, false, false, false, false];

  ttt(id) {
    if (this.modelService.loginService.showToolTips) {
      this.v[id] = !this.v[id];
    }
  }

  get showToolTips(): boolean {
    return this.modelService.loginService.showToolTips;
  }

  get showProToolTips(): boolean {
    return this.modelService.loginService.showProToolTips;
  }

  get showPlaceHolders() {
    return this.modelService.loginService.showPlaceHolders;
  }

  hideName(name: string) {
    return name;
  }

  public async changeVersion(event = null) {
    if (event != null && event.event == undefined) return;

    await this.checkAddons();
  }

  async checkAddons() {
    this.modelService.loginService.customerService.update(this.customer)
      .subscribe((customer) => {
        this.loginService.setCustomer(customer.id);
        this.addonService.checkCurrentCustomerAddons(this.loginService.currentCustomer.id).subscribe((errors) => {
          if (errors.length > 0) {
            this.modelService.systemService.notifyWarning(errors.join('\n'), 10000);
          }
        });

        this.validateLicense().subscribe();
      });
  }

  public updateCustomer(event = null) {

    if (event != null && event.event == undefined) return;
    this.modelService.loginService.customerService.update(this.customer).subscribe((result: Customer) => {
      this.modelService.loginService.currentCustomer = result;
      const key = this.customers.findIndex((x) => x.id == result.id);
      this.customers[key] = result;
    });
  }

  public checkEdit(e) {
    if (
      e.data.email == this.modelService.loginService.currentUser.email ||
      this.modelService.loginService.currentUser.currentClaim == 'ADMIN' ||
      this.modelService.loginService.currentUser.currentClaim == 'MANAGER'
    ) {
      return true;
    }
    return false;
  }

  readonly validateLicense = () => {
    return this.customerService.getLicenseValidation(this.customer.id, this.customer.licenseId).pipe(
      tap((result) => {
        if (!result.isValid) {
          let message = this.translate.instant('Die Lizenz ist ungültig.');
          if (result.currentUsers > result.maxUsers) {
            this.userPageWarning$.next(this.translate.instant('Die maximale Nutzeranzahl der Lizenz ist überschritten.'));
          }

          const unauthorizedAddons = result.currentAddons.filter(addon => !result.authorizedAddons.includes(addon));

          if (unauthorizedAddons.length > 0 || result.currentClassifications > result.maxClassifications) {
            this.addonPageWarning$.next(this.translate.instant('Es sind Addons aktiviert, die nicht in der Lizenz enthalten sind'));
          }

          //this.modelService.systemService.notifyWarning(message);
        } else {
          //this.modelService.systemService.notifyInfo(this.translate.instant('Die Lizenz ist gültig.'));
          this.userPageWarning$.next(undefined);
          this.addonPageWarning$.next(undefined);
        }
      })
    );
  }

  deleteAccountVisible = false;
  deleteConfirmName = '';
  accountToDelete: Customer;
  public deleteCustomer(e) {
    this.deleteConfirmName = '';
    this.accountToDelete = e.row.data as Customer;
    if (this.accountToDelete.id == this.modelService.loginService.currentCustomer.id) {
      this.modelService.systemService.notifyWarning(
        'Es können nur Accounts gelöscht werden, die nicht aktuell ausgewählt sind'
      );
    } else {
      this.deleteAccountVisible = true;
    }
  }

  public confirmDelete() {
    if (this.modelService.loginService.currentCustomer.name == this.accountToDelete.name) {
      this.modelService.systemService.notifyWarning(
        'Es können nur Accounts gelöscht werden, die nicht aktuell ausgewählt sind'
      );
    } else if (
      this.deleteConfirmName == this.accountToDelete.name ||
      (this.deleteConfirmName == '' && this.accountToDelete.name == null)
    ) {
      this.customerService
        .deleteCustomer(this.accountToDelete.id)
        .pipe(
          tap(() => this.modelService.systemService.notifyInfo(this.deleteConfirmName + ' wurde gelöscht ')),
          switchMap(() =>
            this.modelService.loginService.customerService.getAllCustomerByUser(
              this.modelService.loginService.currentUser.id
            )
          )
        )
        .subscribe((result) => (this.customers = result));
    } else {
      this.modelService.systemService.notifyWarning('Accountname passt nicht');
    }

    this.deleteAccountVisible = false;
  }

  myItemTitleTemplate(data, index, container) {
    if (data.disabled) {
      container.parentElement.classList.remove('dx-state-disabled');
      container.parentElement.classList.add('my-disabled-class');
    }
    return 'itemTitleTemplate';
  }

  readonly onAddonCategoryTagBoxInitialized = (category: EditableAddonCategory, event: TagBoxInitializedEvent)  => {
    category.onSelectionStart = () => {
      event.component.open();
    }
  }

  readonly onAddonCategoryTagBoxDisposing = (category: EditableAddonCategory, event: TagBoxDisposingEvent)  => {
    category.onSelectionStart = undefined;
  }

  onAddonSearchChanged(e: { value: string }): void {
    this.addonSearchText = e.value ?? '';
    this.filterAddonCategories();
  }

  private filterAddonCategories(): void {
    const searchTerm = this.addonSearchText.trim().toLowerCase();

    if (!searchTerm) {
      this.filteredAddonCategories = this.addonCategories;
      return;
    }

    this.filteredAddonCategories = this.addonCategories.filter(category =>
      category.translatedName?.toLowerCase().includes(searchTerm) ||
      category.availableAddons.some(addon => addon.name?.toLowerCase().includes(searchTerm)));
  }

  readonly onAddonCategorySelectionStart = (category: EditableAddonCategory, event: ButtonClickEvent) => {

    event.event.stopPropagation();

    if (category.onSelectionStart) {
      category.onSelectionStart();
    }
  }

  readonly mapAddonCategoryToAddonEditorEntry = (category: AddonCategory, license: License, customerAddons: string[]) => {
    const customerCategoryActiveAddons = customerAddons.filter(a => category.addons.includes(a));
    const customerCategoryUnauthorizedAddons = customerCategoryActiveAddons.filter(a => !license.authorizedAddons.includes(a));
    const customerCategoryAvailableAddons = category.addons.filter(addon => license.authorizedAddons.includes(addon)
      || customerCategoryUnauthorizedAddons.includes(addon))
      .map(addon => ({
        addon: addon,
        name: this.loginService.isAdmin ? `${this.translate.instant(addon)} (${addon})` : `${this.translate.instant(addon)}`,
        unlicensed: !license.authorizedAddons.includes(addon),
        tooltip: this.translate.instant('Nicht in Lizenz enthalten')
      })).sort((a, b) => a.name.localeCompare(b.name));

    let translatedName: string;
    let evaluateCategory: (category: EditableAddonCategory, previousAddons: string[], currentAddons: string[]) => { valid: boolean; message?: string; reloadSource?: boolean };

    if (category.isClassification) {
      translatedName = this.translate.instant(category.name);
      evaluateCategory = (category, previousAddons, currentAddons) => {
        const unauthorizedAddons = currentAddons.filter(a => !license.authorizedAddons.includes(a));
        const unselectedAddons = previousAddons.filter(a => !currentAddons.includes(a));
        let valid = license.maxClassifications >= currentAddons.length || currentAddons.length < previousAddons.length;
        let message = this.translate.instant("Die Anzahl der zulässigen Klassifikationen ist bereits erreicht");
        let reloadSource = false;

        for (const addon of unselectedAddons) {
          reloadSource = reloadSource || dropUnauthorizedAddonFromSourceCollections(addon, unauthorizedAddons, customerCategoryAvailableAddons);
        }

        if (unselectedAddons.length > 0 && currentAddons.length < license.maxClassifications && !this.isAdmin) {
          valid = false;
          message = this.translate.instant("Um die bereits zugeordneten Klassifikationen zu entfernen, wenden Sie sich bitte an den Support");
        }

        if (valid) {
          category.summary = license.maxClassifications < MAX_INT_32 ? `(${currentAddons.length}/${license.maxClassifications})`
            : `(${currentAddons.length}/${customerCategoryAvailableAddons.length})`;
        } else {
          category.summary = license.maxClassifications < MAX_INT_32 ? `(${previousAddons.length}/${license.maxClassifications})`
            : `(${previousAddons.length}/${customerCategoryAvailableAddons.length})`;
        }

        category.invalid = (unauthorizedAddons.length > 0)
          || (currentAddons.length > license.maxClassifications);
        category.invalidTooltip = this.translate.instant('Addons in dieser Kategorie sind in der Lizenz nicht enthalten oder die Anzahl der zulässigen Klassifizierungen wurde überschritten');

        return {
          valid,
          message: !valid ? message : undefined,
          reloadSource
        };
      };
    } else if (category.isOutputChannel) {
      translatedName = this.translate.instant(category.name);
      evaluateCategory = (category, previousAddons, currentAddons) => {
        const unauthorizedAddons = currentAddons.filter(a => !license.authorizedAddons.includes(a));
        const unselectedAddons = previousAddons.filter(a => !currentAddons.includes(a));
        let valid = license.maxOutputChannels >= currentAddons.length || currentAddons.length < previousAddons.length;
        let message = this.translate.instant("Die Anzahl der zulässigen Ausgabekanäle ist bereits erreicht")
        let reloadSource = false;

        for (const addon of unselectedAddons) {
          reloadSource = reloadSource || dropUnauthorizedAddonFromSourceCollections(addon, unauthorizedAddons, customerCategoryAvailableAddons);
        }

        if (unselectedAddons.length > 0 && currentAddons.length < license.maxOutputChannels && !this.isAdmin) {
          valid = false;
          message = this.translate.instant("Um die bereits zugeordneten Ausgabekanäle zu entfernen, wenden Sie sich bitte an den Support");
        }

        if (valid) {
          category.summary = license.maxOutputChannels < MAX_INT_32 ? `(${currentAddons.length}/${license.maxOutputChannels})`
            : `(${currentAddons.length}/${customerCategoryAvailableAddons.length})`;
        } else {
          category.summary = license.maxOutputChannels < MAX_INT_32 ? `(${previousAddons.length}/${license.maxOutputChannels})`
            : `(${previousAddons.length}/${customerCategoryAvailableAddons.length})`;
        }

        category.invalid = (unauthorizedAddons.length > 0)
          || (currentAddons.length > license.maxOutputChannels);
        category.invalidTooltip = this.translate.instant('Addons in dieser Kategorie sind in der Lizenz nicht enthalten oder die Anzahl der zulässigen Ausgabekanäle wurde überschritten');

        return {
          valid,
          message: !valid ? message : undefined,
          reloadSource
        };
      };
    } else {
      translatedName = this.translate.instant(category.name);
      evaluateCategory = (category, previousAddons, currentAddons) => {
        const unauthorizedAddons = currentAddons.filter(a => !license.authorizedAddons.includes(a));
        const unselectedAddons = previousAddons.filter(a => !currentAddons.includes(a));
        let reloadSource = false;

        for (const addon of unselectedAddons) {
          reloadSource = reloadSource || dropUnauthorizedAddonFromSourceCollections(addon, unauthorizedAddons, customerCategoryAvailableAddons);
        }

        category.summary = `(${currentAddons.length}/${customerCategoryAvailableAddons.length - unauthorizedAddons.length})`;
        category.invalid = unauthorizedAddons.length > 0;
        category.invalidTooltip = this.translate.instant('Addons in dieser Kategorie sind in der Lizenz nicht enthalten');

        return { valid: true, reloadSource };
      }
    }

    const datasource = new DataSource(new CustomStore({
      key: 'addon',
      loadMode: 'raw',
      load: () => customerCategoryAvailableAddons
    }));

    const onCategoryValueChanged = (category: EditableAddonCategory, e: TagBoxValueChangedEvent) => {

      const result = evaluateCategory(category, e.previousValue, e.value);

      if (category.valueRollback) {
        return;
      }

      if (!result.valid) {
        category.valueRollback = true;
        e.component.option('value', e.previousValue);
        category.valueRollback = false;

        if (result.message) {
          this.modelService.systemService.notifyWarning(result.message);
        }

        return;
      } else {
        this.onCategoryAddonsChanged(e);
      }

      if (result.reloadSource) {
        datasource.reload();
      }
    }

    const editableCategory: EditableAddonCategory = {
      ...category,
      translatedName,
      summary: '',
      invalid: false,
      datasource,
      availableAddons: customerCategoryAvailableAddons,
      selectedAddons: customerCategoryAvailableAddons
        .filter(addon => customerCategoryActiveAddons.includes(addon.addon))
        .map(addon => addon.addon),
      onValueChanged: onCategoryValueChanged
    };

    evaluateCategory(editableCategory, editableCategory.selectedAddons, editableCategory.selectedAddons);

    return editableCategory;
  }
}
